import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  AppBar,
  Box,
  Container,
  Tab,
  Tabs,
  Toolbar,
  Typography,
  IconButton,
  Chip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Avatar,
} from '@mui/material';
import DashboardIcon from '@mui/icons-material/Dashboard';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import SettingsInputAntennaIcon from '@mui/icons-material/SettingsInputAntenna';
import CloudIcon from '@mui/icons-material/Cloud';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import PeopleIcon from '@mui/icons-material/People';
import LogoutIcon from '@mui/icons-material/Logout';
import PersonIcon from '@mui/icons-material/Person';
import LightModeIcon from '@mui/icons-material/LightMode';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import { useThemeMode } from '../App';
import { useAuth, UserRole } from '../contexts/AuthContext';

function MainLayout({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { themeMode, toggleTheme } = useThemeMode();
  const { user, logout, isSuperAdmin, isDemosAdmin } = useAuth();
  const [anchorEl, setAnchorEl] = useState(null);

  const handleTabChange = (event, newValue) => {
    navigate(newValue);
  };

  const handleUserMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    handleUserMenuClose();
    await logout();
    navigate('/login');
  };

  const getTabValue = () => {
    if (location.pathname === '/admin/users') return '/admin/users';
    if (location.pathname === '/admin/touchstream') return '/admin/touchstream';
    if (location.pathname === '/admin/s3') return '/admin/s3';
    if (location.pathname === '/admin/e2e-scenarios') return '/admin/e2e-scenarios';
    if (location.pathname === '/admin') return '/admin';
    return '/dashboard';
  };

  const currentTab = getTabValue();

  // Get role display name
  const getRoleDisplayName = (role) => {
    switch (role) {
      case UserRole.SUPER_ADMIN:
        return 'Super Admin';
      case UserRole.DEMOS_ADMIN:
        return 'Demos Admin';
      case UserRole.DASHBOARD:
        return 'Dashboard User';
      default:
        return role;
    }
  };

  // Get user initials for avatar
  const getUserInitials = () => {
    if (!user?.username) return '?';
    return user.username.substring(0, 2).toUpperCase();
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <AppBar position="static">
        <Toolbar>
          <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
            <Typography
              variant="h6"
              component="div"
              sx={{
                fontWeight: 700,
                background: themeMode === 'dark'
                  ? 'linear-gradient(45deg, #00d4ff 30%, #ffffff 90%)'
                  : 'inherit',
                WebkitBackgroundClip: themeMode === 'dark' ? 'text' : 'inherit',
                WebkitTextFillColor: themeMode === 'dark' ? 'transparent' : 'inherit',
                mr: 2
              }}
            >
              Demo Control Room
            </Typography>
            <Chip
              label="v2.0"
              size="small"
              sx={{
                background: 'rgba(0, 212, 255, 0.2)',
                color: 'white',
                border: '1px solid rgba(0, 212, 255, 0.5)',
                fontSize: '0.75rem'
              }}
            />
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Tabs
              value={currentTab}
              onChange={handleTabChange}
              textColor="inherit"
              indicatorColor="secondary"
              sx={{
                '& .MuiTab-root': {
                  minHeight: 48,
                  textTransform: 'none',
                  fontWeight: 500,
                  fontSize: '0.9rem',
                  transition: 'all 0.3s ease-in-out',
                  '&:hover': {
                    color: themeMode === 'dark' ? '#00d4ff' : '#ffffff',
                    transform: 'translateY(-1px)',
                  },
                },
                '& .MuiTabs-indicator': {
                  height: 3,
                  borderRadius: '2px 2px 0 0',
                  background: themeMode === 'dark'
                    ? 'linear-gradient(90deg, #00d4ff 0%, #ff0080 100%)'
                    : '#ffffff',
                  boxShadow: themeMode === 'dark'
                    ? '0 0 10px rgba(0, 212, 255, 0.5)'
                    : 'none',
                },
              }}
            >
              <Tab
                icon={<DashboardIcon />}
                iconPosition="start"
                label="Dashboard"
                value="/dashboard"
              />
              {isSuperAdmin && (
                <Tab
                  icon={<AdminPanelSettingsIcon />}
                  iconPosition="start"
                  label="Admin"
                  value="/admin"
                />
              )}
              {(isSuperAdmin || isDemosAdmin) && (
                <Tab
                  icon={<SettingsInputAntennaIcon />}
                  iconPosition="start"
                  label="Touchstream"
                  value="/admin/touchstream"
                />
              )}
              {isSuperAdmin && (
                <Tab
                  icon={<CloudIcon />}
                  iconPosition="start"
                  label="S3 Buckets"
                  value="/admin/s3"
                />
              )}
              {(isSuperAdmin || isDemosAdmin) && (
                <Tab
                  icon={<AccountTreeIcon />}
                  iconPosition="start"
                  label="E2E Scenarios"
                  value="/admin/e2e-scenarios"
                />
              )}
              {isSuperAdmin && (
                <Tab
                  icon={<PeopleIcon />}
                  iconPosition="start"
                  label="Users"
                  value="/admin/users"
                />
              )}
            </Tabs>

            <IconButton
              onClick={toggleTheme}
              sx={{
                color: 'white',
                background: 'rgba(255, 255, 255, 0.1)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                transition: 'all 0.3s ease-in-out',
                '&:hover': {
                  background: 'rgba(255, 255, 255, 0.2)',
                  transform: 'rotate(180deg)',
                  boxShadow: themeMode === 'dark'
                    ? '0 0 15px rgba(0, 212, 255, 0.5)'
                    : '0 0 10px rgba(255, 255, 255, 0.3)',
                },
              }}
            >
              {themeMode === 'dark' ? <LightModeIcon /> : <DarkModeIcon />}
            </IconButton>

            {/* User Menu */}
            <IconButton
              onClick={handleUserMenuOpen}
              data-testid="user-menu"
              sx={{
                ml: 1,
                p: 0.5,
              }}
            >
              <Avatar
                sx={{
                  width: 36,
                  height: 36,
                  bgcolor: themeMode === 'dark' ? 'primary.main' : 'secondary.main',
                  fontSize: '0.875rem',
                  fontWeight: 600,
                }}
              >
                {getUserInitials()}
              </Avatar>
            </IconButton>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleUserMenuClose}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              PaperProps={{
                sx: {
                  mt: 1,
                  minWidth: 200,
                },
              }}
            >
              <Box sx={{ px: 2, py: 1.5 }}>
                <Typography variant="subtitle2" fontWeight={600}>
                  {user?.username}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {getRoleDisplayName(user?.role)}
                </Typography>
              </Box>
              <Divider />
              <MenuItem disabled>
                <ListItemIcon>
                  <PersonIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary={user?.email} />
              </MenuItem>
              <Divider />
              <MenuItem onClick={handleLogout}>
                <ListItemIcon>
                  <LogoutIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="Logout" />
              </MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4, flexGrow: 1 }}>
        {children}
      </Container>
    </Box>
  );
}

export default MainLayout;